/**
 * Property Domain Model
 * 
 * Encapsulates business logic and validation rules for properties.
 * Pure business logic - no Firestore operations.
 */

export class Property {
  constructor(data = {}) {
    // Preserve every field from the source document so reads are loss-free —
    // components can rely on raw fields that aren't explicitly modeled below.
    // Writes are unaffected: toFirestore() only serialises the named fields.
    Object.assign(this, data);

    // Core identification
    this.id = data.id || null;
    this.propertyId = data.propertyId || null;
    
    // Address
    this.address = data.address || null;
    this.fullAddress = data.fullAddress || data.address || null;
    this.displayAddress = data.displayAddress || null;
    this.buildingNumber = data.buildingNumber || null;
    this.buildingName = data.buildingName || null;
    this.street = data.street || null;
    this.town = data.town || null;
    this.postcode = data.postcode || null;
    this.addressHash = data.addressHash || null;
    
    // Property details
    this.price = data.price || null;
    this.propertyType = data.propertyType || null;
    this.propertyStatus = data.propertyStatus || 'For Sale';
    this.bedrooms = data.bedrooms || null;
    this.bathrooms = data.bathrooms || null;
    this.tenure = data.tenure || null;
    
    // Location
    this.coordinates = data.coordinates || null;
    this.latitude = data.latitude || data.coordinates?.latitude || null;
    this.longitude = data.longitude || data.coordinates?.longitude || null;
    
    // Agent info
    this.agent = data.agent || null;
    
    // Relationships
    this.jobId = data.jobId || null;
    this.scraperId = data.scraperId || null;
    this.mailshotIds = data.mailshotIds || [];
    
    // Status tracking
    this.deliveryStatus = data.deliveryStatus || 'unknown';
    this.sentHistory = data.sentHistory || [];
    this.isTestProperty = data.isTestProperty || false;
    this.isHistoricalImport = data.isHistoricalImport || false;
    this.doNotContact = data.doNotContact || false; // Compliance: property owner requested no contact
    
    // Merge tracking
    this.mergedInto = data.mergedInto || null;
    this.mergedProperties = data.mergedProperties || [];
    
    // Timestamps
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.fetchedAt = data.fetchedAt || null;
    this.dateAdded = data.dateAdded || null;
    
    // Additional fields
    this.url = data.url || null;
    this.description = data.description || null;
    this.images = data.images || [];
    this.lastSale = data.lastSale || null;
    
    // Land Registry & matching
    this.landRegistryRecord = data.landRegistryRecord || null;
    this.matchConfidence = data.matchConfidence || null; // "high" | "medium" | null
  }

  /**
   * Check if this property can be safely deleted
   * @returns {boolean} - True if property can be deleted
   */
  canBeDeleted() {
    // Cannot delete if it's in any mailshots
    if (this.mailshotIds && this.mailshotIds.length > 0) {
      return false;
    }
    
    // Cannot delete if it's been merged into another property
    if (this.mergedInto) {
      return false;
    }
    
    return true;
  }

  /**
   * Get reason why property cannot be deleted
   * @returns {string|null} - Reason or null if can be deleted
   */
  getDeleteBlockReason() {
    if (this.mailshotIds && this.mailshotIds.length > 0) {
      return `Property is in ${this.mailshotIds.length} mailshot(s)`;
    }
    
    if (this.mergedInto) {
      return `Property has been merged into ${this.mergedInto}`;
    }
    
    return null;
  }

  /**
   * Check if this property can be merged with another
   * @param {Property} other - Another property to check merge compatibility
   * @returns {boolean} - True if properties can be merged
   */
  canBeMergedWith(other) {
    // Cannot merge if either property is already merged
    if (this.mergedInto || other.mergedInto) {
      return false;
    }
    
    // Must have matching address hash
    if (!this.addressHash || !other.addressHash) {
      return false;
    }
    
    if (this.addressHash !== other.addressHash) {
      return false;
    }
    
    // Cannot merge with self
    if (this.id === other.id) {
      return false;
    }
    
    return true;
  }

  /**
   * Check if this property was already sent in a campaign
   * @param {string} campaignId - Campaign ID to check
   * @returns {boolean} - True if property was sent in this campaign
   */
  wasAlreadySentInCampaign(campaignId) {
    if (!this.sentHistory || this.sentHistory.length === 0) {
      return false;
    }
    
    return this.sentHistory.some(entry => entry.campaignId === campaignId);
  }

  /**
   * Check if property is in a specific mailshot
   * @param {string} mailshotId - Mailshot ID to check
   * @returns {boolean} - True if property is in this mailshot
   */
  isInMailshot(mailshotId) {
    return this.mailshotIds && this.mailshotIds.includes(mailshotId);
  }

  /**
   * Check if property can be added to mailshots
   * @returns {boolean} - True if property can be added
   */
  canBeAddedToMailshot() {
    // CRITICAL: Never add banned properties to mailshots
    if (this.doNotContact) {
      return false;
    }
    return true;
  }

  /**
   * Get reason why property cannot be added to mailshot
   * @returns {string|null} - Reason or null if can be added
   */
  getMailshotBlockReason() {
    if (this.doNotContact) {
      return 'Property owner requested no contact';
    }
    return null;
  }

  /**
   * Get list of collections this property affects
   * Used for cascade operations
   * @returns {string[]} - Array of collection names
   */
  getAffectedCollections() {
    const affected = ['discovery_properties'];
    
    if (this.mailshotIds && this.mailshotIds.length > 0) {
      affected.push('discovery_mailshots');
    }
    
    if (this.jobId) {
      affected.push('discovery_scrapes');
    }
    
    return affected;
  }

  /**
   * Validate property data
   * @throws {Error} - If validation fails
   * @returns {boolean} - True if valid
   */
  validate() {
    if (!this.propertyId) {
      throw new Error('propertyId is required');
    }
    
    if (!this.address && !this.fullAddress) {
      throw new Error('address or fullAddress is required');
    }
    
    if (!this.postcode) {
      throw new Error('postcode is required');
    }
    
    // Validate mailshotIds is array
    if (this.mailshotIds && !Array.isArray(this.mailshotIds)) {
      throw new Error('mailshotIds must be an array');
    }
    
    // Validate sentHistory is array
    if (this.sentHistory && !Array.isArray(this.sentHistory)) {
      throw new Error('sentHistory must be an array');
    }
    
    // Validate propertyStatus
    const validStatuses = ['For Sale', 'Sold STC', 'Under Offer', 'Off Market'];
    if (this.propertyStatus && !validStatuses.includes(this.propertyStatus)) {
      throw new Error(`Invalid propertyStatus: ${this.propertyStatus}. Must be one of: ${validStatuses.join(', ')}`);
    }
    
    return true;
  }

  /**
   * Serialize to Firestore document
   * @returns {Object} - Firestore document data
   */
  toFirestore() {
    const doc = {
      propertyId: this.propertyId,
      address: this.address,
      fullAddress: this.fullAddress,
      postcode: this.postcode,
      propertyStatus: this.propertyStatus,
      jobId: this.jobId,
      mailshotIds: this.mailshotIds,
      deliveryStatus: this.deliveryStatus,
      sentHistory: this.sentHistory,
      isTestProperty: this.isTestProperty,
    };
    
    // Optional fields (only include if present)
    if (this.displayAddress) doc.displayAddress = this.displayAddress;
    if (this.buildingNumber) doc.buildingNumber = this.buildingNumber;
    if (this.buildingName) doc.buildingName = this.buildingName;
    if (this.street) doc.street = this.street;
    if (this.town) doc.town = this.town;
    if (this.addressHash) doc.addressHash = this.addressHash;
    if (this.price !== null) doc.price = this.price;
    if (this.propertyType) doc.propertyType = this.propertyType;
    if (this.bedrooms !== null) doc.bedrooms = this.bedrooms;
    if (this.bathrooms !== null) doc.bathrooms = this.bathrooms;
    if (this.tenure) doc.tenure = this.tenure;
    if (this.latitude !== null) doc.latitude = this.latitude;
    if (this.longitude !== null) doc.longitude = this.longitude;
    if (this.coordinates) doc.coordinates = this.coordinates;
    if (this.agent) doc.agent = this.agent;
    if (this.scraperId) doc.scraperId = this.scraperId;
    if (this.mergedInto) doc.mergedInto = this.mergedInto;
    if (this.mergedProperties && this.mergedProperties.length > 0) {
      doc.mergedProperties = this.mergedProperties;
    }
    if (this.isHistoricalImport) doc.isHistoricalImport = this.isHistoricalImport;
    if (this.doNotContact) doc.doNotContact = this.doNotContact;
    if (this.createdAt) doc.createdAt = this.createdAt;
    if (this.updatedAt) doc.updatedAt = this.updatedAt;
    if (this.fetchedAt) doc.fetchedAt = this.fetchedAt;
    if (this.dateAdded) doc.dateAdded = this.dateAdded;
    if (this.url) doc.url = this.url;
    if (this.description) doc.description = this.description;
    if (this.images && this.images.length > 0) doc.images = this.images;
    if (this.lastSale) doc.lastSale = this.lastSale;
    if (this.landRegistryRecord) doc.landRegistryRecord = this.landRegistryRecord;
    if (this.matchConfidence) doc.matchConfidence = this.matchConfidence;
    
    return doc;
  }

  /**
   * Deserialize from Firestore document
   * @param {Object} doc - Firestore document snapshot
   * @returns {Property} - Property instance
   */
  static fromFirestore(doc) {
    if (!doc) return null;
    
    const data = doc.data ? doc.data() : doc;
    const id = doc.id || data.id;
    
    return new Property({
      id,
      ...data
    });
  }

  /**
   * Create a plain object for display (removes internal fields)
   * @returns {Object} - Plain object suitable for JSON serialization
   */
  toJSON() {
    return {
      id: this.id,
      propertyId: this.propertyId,
      address: this.address,
      postcode: this.postcode,
      town: this.town,
      price: this.price,
      propertyType: this.propertyType,
      propertyStatus: this.propertyStatus,
      bedrooms: this.bedrooms,
      mailshotIds: this.mailshotIds,
      deliveryStatus: this.deliveryStatus,
      isTestProperty: this.isTestProperty,
    };
  }
}
